import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Upload, Loader2, Sparkles, X } from "lucide-react";
import api from "@/lib/api";

/**
 * OCRScanner — Upload invoice/receipt image or PDF, scans via Claude Vision,
 * returns extracted fields to the parent via onExtracted({ file_url, extracted }).
 */
export default function OCRScanner({ onExtracted, onCancel }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const camRef = useRef(null);
  const fileRef = useRef(null);

  const handleFile = (f) => {
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("Archivo demasiado grande (máx 10MB)");
      return;
    }
    setFile(f);
    if (f.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(f);
    } else {
      setPreview(null);
    }
  };

  const scan = async () => {
    if (!file) return toast.error("Selecciona una imagen o PDF");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/expenses/scan", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Datos extraídos con IA");
      onExtracted?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al escanear");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="ocr-scanner" className="bg-white border border-slate-200">
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-orange-600" />
          <span className="kbar">Escanear ticket / factura</span>
        </div>
        {onCancel && (
          <button data-testid="ocr-cancel" onClick={onCancel} className="p-1 hover:bg-slate-100">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="p-5 space-y-4">
        {!preview && !file && (
          <div className="grid grid-cols-2 gap-3">
            <button
              data-testid="ocr-camera-btn"
              onClick={() => camRef.current?.click()}
              className="h-28 border-2 border-dashed border-slate-300 hover:border-orange-500 hover:bg-orange-50 flex flex-col items-center justify-center gap-2 transition-colors"
            >
              <Camera className="w-7 h-7 text-slate-500" />
              <span className="text-sm font-semibold text-slate-700">Hacer foto</span>
              <span className="text-[10px] text-slate-400">Desde móvil</span>
            </button>
            <button
              data-testid="ocr-upload-btn"
              onClick={() => fileRef.current?.click()}
              className="h-28 border-2 border-dashed border-slate-300 hover:border-orange-500 hover:bg-orange-50 flex flex-col items-center justify-center gap-2 transition-colors"
            >
              <Upload className="w-7 h-7 text-slate-500" />
              <span className="text-sm font-semibold text-slate-700">Subir archivo</span>
              <span className="text-[10px] text-slate-400">JPG · PNG · PDF</span>
            </button>
          </div>
        )}

        <input
          ref={camRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid="ocr-camera-input"
        />
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,application/pdf"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid="ocr-file-input"
        />

        {preview && (
          <div className="border border-slate-200 bg-slate-50 p-3">
            <img src={preview} alt="Vista previa" className="max-h-64 mx-auto object-contain" />
          </div>
        )}
        {file && !preview && (
          <div className="border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
            <span className="font-semibold">{file.name}</span>
            <span className="text-xs text-slate-500 ml-2">({(file.size / 1024).toFixed(0)} KB)</span>
          </div>
        )}

        {file && (
          <div className="flex gap-2">
            <button
              data-testid="ocr-scan-btn"
              onClick={scan}
              disabled={loading}
              className="btn-brand h-11 flex-1 inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Analizando con IA…</>
              ) : (
                <><Sparkles className="w-4 h-4" /> Extraer datos</>
              )}
            </button>
            <button
              data-testid="ocr-clear-btn"
              onClick={() => { setFile(null); setPreview(null); }}
              disabled={loading}
              className="h-11 px-4 border border-slate-200 hover:border-slate-300 text-sm font-semibold"
            >
              Cambiar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
